import { googleLogout } from '@react-oauth/google';
import { message } from 'antd';
import { devtools } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

import baseConfig from '@/config';
import {
  cancelUser,
  emailLogin,
  getEmailCode,
  getPhoneCode,
  loginOrInsert,
  updatePassword,
} from '@/services/login';
import { ciphertext, getCookie, isValidEmail, removeCookie, setCookie } from '@/utils';
import { userLogout } from '@/utils/eventlog/logAdapter';
import { t } from '@/utils/helper/intl';
import OnlineTimeTracker from '@/utils/OnlineTimeTracker';

import UserStore from '../User';
interface AuthLoginType {
  accessToken?: string;
  accountKey: string; // apple google
  authCode?: string;
  accountId: string; // apple google
}

// 1-输入邮箱和密码UI 2-只输入邮箱UI 3-输入手机验证码UI 4-输入密码UI 5-设置密码UI 6-输入邮箱验证码UI
export enum LoginUIState {
  INPUT_EMAIL_PASSWORD = 1,
  INPUT_EMAIL = 2,
  INPUT_PHONE_CODE = 3,
  INPUT_PASSWORD = 4,
  SET_PASSWORD = 5,
  INPUT_EMAIL_CODE = 6,
}

export type LoginStoreProps = {
  sendCodeState: boolean;
  isNeedSetupPassword: boolean;
  loginUIState: LoginUIState;
  countDown: number;
  salt?: string;
  startCountDown(accountId: string): void;
  getPhoneCode(accountId: string): Promise<any>;
  timer: NodeJS.Timeout | undefined;
  setUIState: (value: number) => void;
  checkLoginAccount(
    accountId: string,
    t: (id: string, values?: Record<string, any>) => any,
  ): void;
  loginWithCode(accountId: string, code: string): Promise<any>;
  savePassword(accountId: string, password: string): void;
  loginLoading: boolean;
  authLogin(params: AuthLoginType): Promise<any>;
  logout(): void;
  resetLoginStore: any;
  justCancelUser: () => void;

  isNewUser?: boolean; // 是否新用户
  setIsNewUser?: (value: boolean) => void; // 设置是否新用户
};

// const useLoginStore = createWithEqualityFn<LoginStoreProps>()(
const useLoginStore = createWithEqualityFn<any>()(
  devtools(
    (set, get) => ({
      isNewUser: false, // 是否新用户
      setIsNewUser: (value: boolean) => {
        set({ isNewUser: value });
      },

      timer: undefined,
      sendCodeState: false,
      countDown: 60,
      isNeedSetupPassword: false,
      salt: undefined,
      // 点击发送验证码
      startCountDown: async (accountId: string) => {
        const { sendCodeState, getPhoneCode } = get();
        if (sendCodeState) return;
        set({ sendCodeState: true });
        try {
          const sendStatus = await getPhoneCode(accountId);
          if (sendStatus.code === 200) {
            // 立即更新一次倒计时
            set({ countDown: 59 }); // 从59开始，因为已经过去1秒

            const startTimer = () => {
              const { countDown } = get();
              if (countDown <= 1) {
                // 重置倒计时秒数
                set({ sendCodeState: false, countDown: 60 });
                return;
              }
              set({ countDown: countDown - 1 });
              // 设置下一次timer
              const timer = setTimeout(startTimer, 1000);
              set({ timer });
            };
            // 启动首次timer
            const timer = setTimeout(startTimer, 1000);
            set({ timer });
          } else {
            set({ sendCodeState: false });
          }
          return sendStatus;
        } catch (e) {
          set({ sendCodeState: false });
          message.error('网络错误');
        }
      },
      // 点击发送邮箱验证码
      startEmailCountDown: async (accountId: string) => {
        const { sendCodeState } = get();
        if (sendCodeState) return;
        try {
          const sendStatus = await getEmailCode(accountId);
          if (sendStatus.code === 200 || sendStatus.code === 10103011) {
            set({ sendCodeState: true });

            // 立即更新一次倒计时
            set({ countDown: 59 }); // 从59开始，因为已经过去1秒

            const startTimer = () => {
              const { countDown } = get();
              if (countDown <= 1) {
                // 重置倒计时秒数
                set({ sendCodeState: false, countDown: 60 });
                return;
              }
              set({ countDown: countDown - 1 });
              // 设置下一次timer
              const timer = setTimeout(startTimer, 1000);
              set({ timer });
            };
            // 启动首次timer
            const timer = setTimeout(startTimer, 1000);
            set({ timer });
          } else {
            set({ sendCodeState: false });
          }
          return sendStatus;
        } catch (e) {
          set({ sendCodeState: false });
          message.error('网络错误');
        }
      },
      // 获取验证码
      getPhoneCode: async (accountId: string) => {
        try {
          const res = await getPhoneCode({ phone: accountId });
          return res;
        } catch (error) {
          console.log(error);
          message.error('发送失败');
          set({ sendCodeState: false });
        }
      },
      loginLoading: false,
      loginUIState: 1,
      setUIState: (value: number) => {
        set({ loginUIState: value });
      },
      loginWithCode: async (accountId: string, code: string) => {
        set({ loginLoading: true });

        let loginRes;
        if (isValidEmail(accountId)) {
          loginRes = await emailLogin({
            accountId,
            code: code,
          });
        } else {
          // 手机验证码登录
          loginRes = await loginOrInsert({
            accountId: accountId,
            code: code,
            accountType: 48,
          });
        }

        console.log('loginRes', loginRes);
        if (loginRes?.code !== 200) {
          set({ loginLoading: false });
          loginRes.code = loginRes.data.code;
          return loginRes || {};
        }

        let accountType = 48;
        if (isValidEmail(accountId)) {
          accountType = 60;
        }
        // 登录成功 保存token信息
        setCookie('user', loginRes.data, 30);

        set({ loginLoading: false });

        return loginRes;
      },
      savePassword: async (accountId: string, password: string, oldSalt: '') => {
        set({ loginLoading: true });
        const { salt, newPassword } = await ciphertext(oldSalt, password);

        const updatePasswordRes = await updatePassword({
          accountId: accountId,
          salt,
          newPassword,
        });
        set({ loginLoading: false });
        return updatePasswordRes;
      },

      // 要保留的key
      ignoreKey: ['cookie_agree', 'taskCardShown'],
      // 退出登录
      logout: () => {
        const { timer } = get();

        removeCookie('user');
        removeCookie('userInfo');
        // localStorage.clear();
        // 获取 localStorage 中所有的 keys
        const keys = Object.keys(localStorage);

        // 遍历 keys 并删除除 "cookie_agree" 之外的其他数据
        keys.forEach((key) => {
          if (!get().ignoreKey.includes(key)) {
            localStorage.removeItem(key);
          }
        });
        googleLogout();
        // 重置状态
        set({ sendCodeState: false, countDown: 60 });
        // 清除定时器
        clearInterval(timer);
        const tracker = OnlineTimeTracker.getInstance();
        tracker.destroy();
        userLogout().finally(() => {
          // 刷新当前页面而不是跳转到 home
          window.location.reload();
        });
      },
      resetLoginStore: () => {
        set({
          agreeState: false,
          timer: undefined,
          sendCodeState: false,
          countDown: 60,
          loginLoading: false,
          userInfo: { avatarUrl: null, accountId: null, uid: null },
        });
      },

      // 注销用户
      justCancelUser: async () => {
        let params = {
          accountType: getCookie('user').accountType,
          accountId: getCookie('user').accountId,
          deadline: 3,
        };

        let rsp = await cancelUser(params);
        if (rsp.success) {
          message.success(t('fc.38e1e66e'));
          await UserStore.getState().getUserBenefit(false);
        }
      },
    }),
    {
      name: 'login_store',
      enabled: baseConfig.debuggable,
    },
  ),
  shallow,
);

export default useLoginStore;
